<template>
    <div class="product-page periscope-ai-page">
        <section class="periscope-ai-hero">
            <div class="periscope-ai-shell periscope-ai-hero__layout">
                <div class="periscope-ai-hero__copy">
                    <p class="periscope-ai-hero__product">Periscope AI</p>
                    <h1>Turn underwater video into actionable information.</h1>
                    <p class="periscope-ai-hero__summary">
                        Fathom combines persistent underwater imaging, site-specific models and human verification to help teams detect defined events—and act on what happens beneath the surface.
                    </p>

                    <ul class="periscope-ai-hero__concepts" aria-label="Key capabilities">
                        <li>Persistent monitoring</li>
                        <li>Site-specific detection</li>
                        <li>Human-verified results</li>
                    </ul>
                </div>

                <figure class="periscope-ai-hero__media">
                    <MotionMedia
                        class="periscope-ai-hero__video"
                        :src="heroVideo"
                        :poster="heroPoster"
                    />
                </figure>
            </div>
        </section>

        <section class="periscope-ai-intro">
            <div class="periscope-ai-shell periscope-ai-intro__layout">
                <div>
                    <p class="periscope-ai-eyebrow periscope-ai-eyebrow--dark">From camera to decision</p>
                    <h2>AI is only useful when the underwater view is reliable.</h2>
                </div>

                <div class="periscope-ai-intro__copy">
                    <p>Underwater AI begins with consistent, usable imagery. Fathom’s self-cleaning camera systems keep watching while our monitoring workflow collects, evaluates and organizes the visual evidence around a specific operational objective.</p>
                    <p>The result is more than a camera feed: it is a repeatable path from live video to alerts, structured data and documented outcomes.</p>
                </div>
            </div>
        </section>

        <section class="periscope-ai-workflow" aria-label="AI monitoring workflow">
            <div class="periscope-ai-shell periscope-ai-workflow__list">
                <article class="periscope-ai-workflow__step">
                    <span>01</span>
                    <h3>Capture dependable imagery</h3>
                    <p>Self-cleaning underwater cameras provide the persistent visual data required for long-term monitoring.</p>
                </article>

                <article class="periscope-ai-workflow__step">
                    <span>02</span>
                    <h3>Train for the objective</h3>
                    <p>Models are developed and tested for a defined subject, location and set of operating conditions.</p>
                </article>

                <article class="periscope-ai-workflow__step">
                    <span>03</span>
                    <h3>Detect and alert</h3>
                    <p>Local processing evaluates incoming imagery and sends event notifications with the context teams need to respond.</p>
                </article>

                <article class="periscope-ai-workflow__step">
                    <span>04</span>
                    <h3>Verify and refine</h3>
                    <p>Human review confirms events and turns real-world results into a stronger, site-specific workflow over time.</p>
                </article>
            </div>
        </section>

        <section class="periscope-ai-case-study">
            <div class="periscope-ai-shell">
                <div class="periscope-ai-case-study__story">
                    <div class="periscope-ai-case-study__copy">
                        <p class="periscope-ai-eyebrow">Operational case study</p>
                        <h2>AI-assisted turtle monitoring at a Florida coastal power facility.</h2>
                        <p>A 24-camera underwater network spans four intake bays. Local computers process six camera feeds each, evaluate potential turtle events and send alerts for human verification.</p>
                        <p>After 18 months of model development and testing, the operational detection workflow launched in April 2026. Events are reviewed by facility personnel, the onsite project lead and Fathom.</p>
                        <p class="periscope-ai-case-study__validation">Tested on held-out data and in live operations</p>
                    </div>

                    <figure class="periscope-ai-case-study__media">
                        <img src="@/assets/images/turtle.png" alt="Underwater turtle event marked for verification">
                    </figure>
                </div>

                <div class="periscope-ai-metrics" aria-label="Case study metrics">
                    <div class="periscope-ai-metric"><strong>24</strong><span>underwater cameras</span></div>
                    <div class="periscope-ai-metric"><strong>4</strong><span>intake bays monitored</span></div>
                    <div class="periscope-ai-metric"><strong>~30</strong><span>human-verified turtle events*</span></div>
                    <div class="periscope-ai-metric periscope-ai-metric--wide">
                        <strong>1–2 <small>per day</small></strong>
                        <span>current false alerts; early trial materials anticipated approximately four per hour</span>
                    </div>
                </div>

                <p class="periscope-ai-metrics__note">*Approximate project total. Client name and precise location withheld.</p>
            </div>
        </section>

        <section class="periscope-ai-outputs">
            <div class="periscope-ai-shell periscope-ai-outputs__layout">
                <div class="periscope-ai-outputs__heading">
                    <p class="periscope-ai-eyebrow periscope-ai-eyebrow--dark">What customers receive</p>
                    <h2>Evidence your team can use.</h2>
                    <p>Monitoring outputs are designed to support operational response, review and reporting—not simply generate another stream of video.</p>
                </div>

                <div class="periscope-ai-outputs__list">
                    <article><span>01</span><div><h3>Detection imagery</h3><p>Images and event clips tied to the alert</p></div></article>
                    <article><span>02</span><div><h3>Event context</h3><p>Timestamp, camera ID and confidence score</p></div></article>
                    <article><span>03</span><div><h3>Structured data</h3><p>CSV detection records for analysis and archiving</p></div></article>
                    <article><span>04</span><div><h3>Reporting</h3><p>Monthly summaries of monitoring activity and results</p></div></article>
                    <article><span>05</span><div><h3>System visibility</h3><p>Camera status and scene-condition indicators</p></div></article>
                    <article><span>06</span><div><h3>Flexible oversight</h3><p>Customer verification or managed monitoring by Fathom</p></div></article>
                </div>
            </div>
        </section>

        <section class="periscope-ai-principles">
            <div class="periscope-ai-shell periscope-ai-principles__layout">
                <article class="periscope-ai-principle periscope-ai-principle--primary">
                    <p class="periscope-ai-eyebrow">Built for a defined objective</p>
                    <h2>Purpose-trained, not one-size-fits-all.</h2>
                    <p>Species, visibility, lighting, background movement and response needs vary by site. Fathom develops monitoring workflows around the question the customer actually needs answered.</p>
                </article>

                <article class="periscope-ai-principle periscope-ai-principle--secondary">
                    <p class="periscope-ai-eyebrow">Our approach</p>
                    <h3>Human judgment stays in the loop.</h3>
                    <p>AI identifies potential events and organizes evidence. Trained people verify the result and determine the appropriate response. That combination makes the system practical for real-world operations.</p>
                </article>
            </div>
        </section>

        <section class="periscope-ai-applications">
            <div class="periscope-ai-shell periscope-ai-applications__layout">
                <div class="periscope-ai-applications__copy">
                    <p class="periscope-ai-eyebrow">Designed to extend</p>
                    <h2>One monitoring foundation. Many defined objectives.</h2>
                    <p>The same capture, detection, review and reporting workflow can be configured for new projects as training data and validation requirements are established.</p>
                </div>

                <div class="periscope-ai-applications__list">
                    <span>Critical infrastructure</span>
                    <span>Marine research</span>
                    <span>Conservation</span>
                    <span>Habitat monitoring</span>
                </div>
            </div>
        </section>

        <section class="periscope-ai-cta">
            <div class="periscope-ai-shell periscope-ai-cta__content">
                <p class="periscope-ai-eyebrow">Start with the question</p>
                <h2>What do you need to see, detect or document underwater?</h2>
                <p>Tell us the objective, site conditions and response requirements. We’ll help define the monitoring system and the evidence it should deliver.</p>
                <b-button class="periscope-ai-cta__button" @click="$router.push('/contact')">Discuss your monitoring objective</b-button>
            </div>
        </section>
    </div>
</template>

<script>
import MotionMedia from '@/components/journey/MotionMedia.vue'
import heroVideo from '@/assets/videos/vitb-machine-vision-demo.mp4'
import heroPoster from '@/assets/images/video-posters/vitb-machine-vision-poster.jpg'

export default {
    name: 'Periscope',
    components: { MotionMedia },
    data() {
        return {
            heroVideo,
            heroPoster,
        }
    },
}
</script>

<style>
#app .periscope-ai-page {
    overflow: hidden;
    color: #ffffff;
    background: #071323;
}

#app .periscope-ai-page *,
#app .periscope-ai-page *::before,
#app .periscope-ai-page *::after { box-sizing: border-box; }

#app .periscope-ai-shell {
    width: min(calc(100% - clamp(40px, 8vw, 148px)), 1600px);
    margin: 0 auto;
}

#app .periscope-ai-eyebrow {
    margin: 0 0 24px;
    color: #cfedf8;
    font-size: 0.78rem;
    font-weight: 650;
    line-height: 1.3;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

#app .periscope-ai-eyebrow--dark { color: #0b2851; }

#app .periscope-ai-hero {
    position: relative;
    min-height: min(960px, 100svh);
    padding: clamp(148px, 14vh, 190px) 0 clamp(80px, 9vh, 116px);
    background: #050d18;
}

#app .periscope-ai-hero::before {
    position: absolute;
    inset: 0;
    content: "";
    background: linear-gradient(115deg, rgba(11, 40, 81, 0.18), transparent 44%);
    pointer-events: none;
}

#app .periscope-ai-hero__layout {
    position: relative;
    display: grid;
    min-height: 650px;
    grid-template-columns: minmax(0, 0.9fr) minmax(460px, 1.1fr);
    gap: clamp(64px, 8vw, 148px);
    align-items: center;
}

#app .periscope-ai-hero__copy { position: relative; z-index: 2; }

#app .periscope-ai-hero__product {
    margin: 0 0 28px;
    color: #ffffff;
    font-size: clamp(1.2rem, 1.6vw, 1.5rem);
    font-weight: 560;
    letter-spacing: -0.02em;
}

#app .periscope-ai-hero h1 {
    max-width: 9.5ch;
    margin: 0 0 36px;
    color: #ffffff;
    font-size: clamp(4rem, 6.2vw, 7.2rem);
    font-weight: 500;
    line-height: 0.92;
    letter-spacing: -0.064em;
}

#app .periscope-ai-hero__summary {
    max-width: 610px;
    margin: 0;
    color: #c7d2d8;
    font-size: clamp(1.08rem, 1.3vw, 1.28rem);
    line-height: 1.72;
}

#app .periscope-ai-hero__concepts {
    display: flex;
    margin: clamp(52px, 7vh, 76px) 0 0;
    padding: 0;
    border-top: 1px solid rgba(207, 237, 248, 0.26);
    list-style: none;
}

#app .periscope-ai-hero__concepts li {
    flex: 1 1 0;
    padding: 20px 24px 0 0;
    color: #ffffff;
    font-size: 0.86rem;
    line-height: 1.45;
}

#app .periscope-ai-hero__media {
    position: relative;
    min-width: 0;
    height: clamp(500px, 61vh, 690px);
    margin: 0;
    overflow: hidden;
    background: #071323;
}

#app .periscope-ai-hero__media::after {
    position: absolute;
    inset: 0;
    content: "";
    background: linear-gradient(90deg, #050d18 0%, transparent 24%), linear-gradient(0deg, rgba(5, 13, 24, 0.36), transparent 40%);
    pointer-events: none;
}

#app .periscope-ai-hero__media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 56% 50%;
    filter: saturate(0.72) contrast(1.08) brightness(0.82);
    transform: scale(1.015);
}

#app .periscope-ai-intro {
    padding: clamp(104px, 11vw, 176px) 0;
    color: #071323;
    background: #f7f2f5;
}

#app .periscope-ai-intro__layout {
    display: grid;
    grid-template-columns: minmax(0, 1.02fr) minmax(320px, 0.68fr);
    gap: clamp(64px, 10vw, 180px);
    align-items: start;
}

#app .periscope-ai-intro h2 {
    max-width: 12ch;
    margin: 0;
    color: #071323;
    font-size: clamp(3.5rem, 5.7vw, 6.8rem);
    font-weight: 500;
    line-height: 0.96;
    letter-spacing: -0.058em;
}

#app .periscope-ai-intro__copy { padding-top: 54px; }

#app .periscope-ai-intro__copy p {
    max-width: 55ch;
    margin: 0 0 28px;
    color: #344451;
    font-size: clamp(1.08rem, 1.25vw, 1.24rem);
    line-height: 1.78;
}

#app .periscope-ai-workflow {
    padding: clamp(96px, 10vw, 154px) 0;
    background: #071323;
}

#app .periscope-ai-workflow__list { border-top: 1px solid rgba(255, 255, 255, 0.18); }

#app .periscope-ai-workflow__step {
    display: grid;
    padding: clamp(34px, 4vw, 58px) 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    grid-template-columns: 90px minmax(250px, 0.9fr) minmax(320px, 1fr);
    gap: clamp(28px, 5vw, 90px);
    align-items: baseline;
}

#app .periscope-ai-workflow__step > span {
    color: #7accc7;
    font-size: 0.78rem;
    font-weight: 650;
    letter-spacing: 0.12em;
}

#app .periscope-ai-workflow__step h3 {
    margin: 0;
    color: #ffffff;
    font-size: clamp(1.85rem, 2.7vw, 3.15rem);
    font-weight: 500;
    line-height: 1.05;
    letter-spacing: -0.04em;
}

#app .periscope-ai-workflow__step p {
    max-width: 55ch;
    margin: 0;
    color: #aebdc5;
    font-size: 1.05rem;
    line-height: 1.72;
}

#app .periscope-ai-case-study {
    padding: clamp(108px, 11vw, 180px) 0 clamp(92px, 9vw, 142px);
    background: #0b2851;
}

#app .periscope-ai-case-study__story {
    display: grid;
    grid-template-columns: minmax(0, 0.88fr) minmax(420px, 1.12fr);
    gap: clamp(66px, 9vw, 152px);
    align-items: center;
}

#app .periscope-ai-case-study__copy h2 {
    max-width: 12ch;
    margin: 0 0 42px;
    color: #ffffff;
    font-size: clamp(3.25rem, 5.25vw, 6.4rem);
    font-weight: 500;
    line-height: 0.97;
    letter-spacing: -0.056em;
}

#app .periscope-ai-case-study__copy > p:not(.periscope-ai-eyebrow):not(.periscope-ai-case-study__validation) {
    max-width: 57ch;
    margin: 0 0 24px;
    color: #c7d2d8;
    font-size: 1.08rem;
    line-height: 1.72;
}

#app .periscope-ai-case-study__validation {
    display: inline-block;
    margin: 20px 0 0;
    padding: 14px 0 0;
    border-top: 1px solid rgba(207, 237, 248, 0.38);
    color: #cfedf8;
    font-size: 0.9rem;
    font-weight: 620;
    line-height: 1.45;
}

#app .periscope-ai-case-study__media {
    position: relative;
    height: clamp(480px, 56vw, 690px);
    margin: 0;
    overflow: hidden;
    background: #071323;
}

#app .periscope-ai-case-study__media::after {
    position: absolute;
    inset: 0;
    content: "";
    background: linear-gradient(0deg, rgba(7, 19, 35, 0.48), transparent 52%);
    pointer-events: none;
}

#app .periscope-ai-case-study__media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    filter: saturate(0.78) contrast(1.06);
}

#app .periscope-ai-metrics {
    display: grid;
    margin-top: clamp(74px, 8vw, 122px);
    border-top: 1px solid rgba(255, 255, 255, 0.24);
    border-bottom: 1px solid rgba(255, 255, 255, 0.24);
    grid-template-columns: repeat(3, minmax(0, 0.7fr)) minmax(280px, 1.25fr);
}

#app .periscope-ai-metric {
    display: flex;
    min-height: 205px;
    padding: 30px clamp(20px, 2.5vw, 40px) 32px 0;
    border-right: 1px solid rgba(255, 255, 255, 0.2);
    flex-direction: column;
    justify-content: space-between;
}

#app .periscope-ai-metric + .periscope-ai-metric { padding-left: clamp(20px, 2.5vw, 40px); }
#app .periscope-ai-metric:last-child { padding-right: 0; border-right: 0; }

#app .periscope-ai-metric strong {
    color: #ffffff;
    font-size: clamp(3.5rem, 5vw, 6.2rem);
    font-weight: 480;
    line-height: 0.9;
    letter-spacing: -0.06em;
}

#app .periscope-ai-metric strong small {
    font-size: 0.3em;
    font-weight: 550;
    letter-spacing: -0.02em;
}

#app .periscope-ai-metric > span {
    max-width: 30ch;
    color: #c7d2d8;
    font-size: 0.93rem;
    line-height: 1.55;
}

#app .periscope-ai-metrics__note {
    margin: 18px 0 0;
    color: #aebdc5;
    font-size: 0.82rem;
    line-height: 1.5;
}

#app .periscope-ai-outputs {
    padding: clamp(112px, 12vw, 192px) 0;
    color: #071323;
    background: #f7f2f5;
}

#app .periscope-ai-outputs__layout {
    display: grid;
    grid-template-columns: minmax(280px, 0.7fr) minmax(520px, 1.3fr);
    gap: clamp(72px, 11vw, 184px);
    align-items: start;
}

#app .periscope-ai-outputs__heading { position: sticky; top: 130px; }

#app .periscope-ai-outputs__heading h2 {
    max-width: 9ch;
    margin: 0 0 34px;
    color: #071323;
    font-size: clamp(3.5rem, 5vw, 6rem);
    font-weight: 500;
    line-height: 0.95;
    letter-spacing: -0.055em;
}

#app .periscope-ai-outputs__heading > p:last-child {
    max-width: 45ch;
    margin: 0;
    color: #344451;
    font-size: 1.08rem;
    line-height: 1.72;
}

#app .periscope-ai-outputs__list { border-top: 1px solid rgba(7, 19, 35, 0.28); }

#app .periscope-ai-outputs__list article {
    display: grid;
    padding: 28px 0 30px;
    border-bottom: 1px solid rgba(7, 19, 35, 0.2);
    grid-template-columns: 60px 1fr;
    gap: 24px;
}

#app .periscope-ai-outputs__list article > span {
    padding-top: 5px;
    color: #0b2851;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
}

#app .periscope-ai-outputs__list h3 {
    margin: 0 0 8px;
    color: #071323;
    font-size: clamp(1.5rem, 2vw, 2.2rem);
    font-weight: 540;
    line-height: 1.12;
    letter-spacing: -0.035em;
}

#app .periscope-ai-outputs__list p {
    margin: 0;
    color: #53616a;
    font-size: 1rem;
    line-height: 1.6;
}

#app .periscope-ai-principles {
    padding: clamp(108px, 11vw, 178px) 0;
    background: #071323;
}

#app .periscope-ai-principles__layout {
    display: grid;
    grid-template-columns: minmax(0, 1.16fr) minmax(340px, 0.84fr);
    gap: clamp(30px, 4vw, 66px);
    align-items: stretch;
}

#app .periscope-ai-principle { padding: clamp(48px, 6vw, 86px); }
#app .periscope-ai-principle--primary { color: #ffffff; background: #0b2851; }

#app .periscope-ai-principle--secondary {
    align-self: end;
    padding-right: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.24);
    background: transparent;
}

#app .periscope-ai-principle h2 {
    max-width: 10ch;
    margin: 0 0 34px;
    color: #ffffff;
    font-size: clamp(3.1rem, 4.8vw, 5.7rem);
    font-weight: 500;
    line-height: 0.96;
    letter-spacing: -0.055em;
}

#app .periscope-ai-principle h3 {
    max-width: 11ch;
    margin: 0 0 30px;
    color: #ffffff;
    font-size: clamp(2.5rem, 3.7vw, 4.5rem);
    font-weight: 500;
    line-height: 1;
    letter-spacing: -0.05em;
}

#app .periscope-ai-principle > p:last-child {
    max-width: 52ch;
    margin: 0;
    color: #c7d2d8;
    font-size: 1.06rem;
    line-height: 1.72;
}

#app .periscope-ai-applications {
    padding: clamp(108px, 11vw, 176px) 0;
    background: #050d18;
}

#app .periscope-ai-applications__layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(360px, 0.72fr);
    gap: clamp(76px, 12vw, 200px);
    align-items: end;
}

#app .periscope-ai-applications__copy h2 {
    max-width: 13ch;
    margin: 0 0 36px;
    color: #ffffff;
    font-size: clamp(3.4rem, 5.6vw, 6.8rem);
    font-weight: 500;
    line-height: 0.95;
    letter-spacing: -0.058em;
}

#app .periscope-ai-applications__copy > p:last-child {
    max-width: 58ch;
    margin: 0;
    color: #aebdc5;
    font-size: 1.06rem;
    line-height: 1.72;
}

#app .periscope-ai-applications__list { border-top: 1px solid rgba(255, 255, 255, 0.22); }

#app .periscope-ai-applications__list span {
    display: block;
    padding: 24px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    color: #ffffff;
    font-size: clamp(1.35rem, 2vw, 2.1rem);
    font-weight: 500;
    line-height: 1.2;
    letter-spacing: -0.025em;
}

#app .periscope-ai-cta {
    padding: clamp(114px, 12vw, 190px) 0;
    background: #0b2851;
}

#app .periscope-ai-cta__content { max-width: 1260px; }

#app .periscope-ai-cta h2 {
    max-width: 14ch;
    margin: 0 0 36px;
    color: #ffffff;
    font-size: clamp(3.5rem, 6.2vw, 7.4rem);
    font-weight: 500;
    line-height: 0.94;
    letter-spacing: -0.06em;
}

#app .periscope-ai-cta__content > p:not(.periscope-ai-eyebrow) {
    max-width: 58ch;
    margin: 0 0 44px;
    color: #c7d2d8;
    font-size: clamp(1.08rem, 1.25vw, 1.2rem);
    line-height: 1.7;
}

#app .periscope-ai-cta__button.button,
#app .periscope-ai-cta__button.btn {
    min-height: 54px;
    padding: 15px 26px;
    border: 1px solid #ff0559;
    border-radius: 0;
    color: #ffffff;
    background: #ff0559;
    box-shadow: none;
    font-size: 0.96rem;
    font-weight: 650;
    letter-spacing: 0;
    text-transform: none;
}

#app .periscope-ai-cta__button.button:hover,
#app .periscope-ai-cta__button.button:focus-visible,
#app .periscope-ai-cta__button.btn:hover,
#app .periscope-ai-cta__button.btn:focus-visible {
    border-color: #ffffff;
    color: #071323;
    background: #ffffff;
    transform: translateY(-2px);
}

@media (max-width: 1100px) {
    #app .periscope-ai-hero { min-height: auto; }

    #app .periscope-ai-hero__layout,
    #app .periscope-ai-case-study__story { grid-template-columns: 1fr; }

    #app .periscope-ai-hero__media { height: min(70vw, 620px); }
    #app .periscope-ai-hero__media::after { background: linear-gradient(0deg, rgba(5, 13, 24, 0.46), transparent 48%); }

    #app .periscope-ai-intro__layout,
    #app .periscope-ai-outputs__layout,
    #app .periscope-ai-applications__layout {
        grid-template-columns: 1fr;
        gap: 60px;
    }

    #app .periscope-ai-intro__copy { padding-top: 0; }

    #app .periscope-ai-workflow__step {
        grid-template-columns: 64px minmax(220px, 0.8fr) minmax(280px, 1fr);
    }

    #app .periscope-ai-case-study__media { height: min(70vw, 650px); }
    #app .periscope-ai-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    #app .periscope-ai-metric:nth-child(2) { border-right: 0; }
    #app .periscope-ai-metric:nth-child(-n + 2) { border-bottom: 1px solid rgba(255, 255, 255, 0.2); }
    #app .periscope-ai-metric:nth-child(3) { padding-left: 0; }
    #app .periscope-ai-outputs__heading { position: static; }

    #app .periscope-ai-principles__layout {
        grid-template-columns: minmax(0, 1.08fr) minmax(300px, 0.92fr);
    }
}

@media (max-width: 760px) {
    #app .periscope-ai-shell { width: calc(100% - 40px); }

    #app .periscope-ai-hero {
        padding-top: 112px;
        padding-bottom: 82px;
    }

    #app .periscope-ai-hero__layout { min-height: 0; gap: 52px; }

    #app .periscope-ai-hero h1 {
        max-width: 10ch;
        font-size: clamp(3.45rem, 14.2vw, 5.3rem);
    }

    #app .periscope-ai-hero__concepts { display: block; }

    #app .periscope-ai-hero__concepts li {
        padding: 14px 0;
        border-bottom: 1px solid rgba(207, 237, 248, 0.16);
    }

    #app .periscope-ai-hero__media { height: min(96vw, 500px); }

    #app .periscope-ai-intro h2,
    #app .periscope-ai-case-study__copy h2,
    #app .periscope-ai-outputs__heading h2,
    #app .periscope-ai-applications__copy h2,
    #app .periscope-ai-cta h2 { font-size: clamp(3rem, 13vw, 4.6rem); }

    #app .periscope-ai-workflow__step {
        grid-template-columns: 44px 1fr;
        gap: 18px 22px;
    }

    #app .periscope-ai-workflow__step p { grid-column: 2; }
    #app .periscope-ai-case-study__media { height: min(110vw, 540px); }
    #app .periscope-ai-metrics { grid-template-columns: 1fr; }

    #app .periscope-ai-metric,
    #app .periscope-ai-metric + .periscope-ai-metric,
    #app .periscope-ai-metric:nth-child(3) {
        min-height: 165px;
        padding: 26px 0;
        border-right: 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    }

    #app .periscope-ai-metric:last-child { border-bottom: 0; }
    #app .periscope-ai-principles__layout { grid-template-columns: 1fr; }
    #app .periscope-ai-principle { padding: 48px 32px; }

    #app .periscope-ai-principle--secondary {
        padding-right: 0;
        padding-left: 0;
    }
}

@media (max-width: 480px) {
    #app .periscope-ai-shell { width: calc(100% - 36px); }

    #app .periscope-ai-hero h1 {
        font-size: clamp(3.15rem, 15.5vw, 4.5rem);
        letter-spacing: -0.058em;
    }

    #app .periscope-ai-hero__media,
    #app .periscope-ai-case-study__media { height: 118vw; }

    #app .periscope-ai-outputs__list article {
        grid-template-columns: 42px 1fr;
        gap: 14px;
    }

    #app .periscope-ai-principle { padding: 42px 24px; }

    #app .periscope-ai-cta__button.button,
    #app .periscope-ai-cta__button.btn {
        width: 100%;
        white-space: normal;
    }
}

@media (prefers-reduced-motion: reduce) {
    #app .periscope-ai-hero__media img,
    #app .periscope-ai-cta__button.button,
    #app .periscope-ai-cta__button.btn {
        transition: none;
        transform: none;
    }
}
</style>

<style>
/* Periscope AI visual refinement */
#app .periscope-ai-page {
    background: #030914;
}

#app .periscope-ai-hero {
    min-height: calc(100svh - 88px);
    padding: clamp(94px, 10vh, 124px) 0 clamp(70px, 8vh, 92px);
    overflow: hidden;
    background: #030914;
}

#app .periscope-ai-hero::before {
    background: linear-gradient(112deg, rgba(11, 40, 81, 0.2), transparent 42%);
}

#app .periscope-ai-hero__layout {
    width: min(calc(100% - clamp(40px, 8vw, 148px)), 1420px);
    min-height: min(620px, calc(100svh - 248px));
    grid-template-columns: minmax(450px, 0.78fr) minmax(0, 1.22fr);
    gap: clamp(42px, 4.5vw, 72px);
}

#app .periscope-ai-hero__copy {
    padding: 14px 0 18px;
}

#app .periscope-ai-hero__product {
    margin-bottom: 24px;
}

#app .periscope-ai-hero h1 {
    max-width: 8.8ch;
    margin-bottom: 34px;
    font-size: clamp(4.15rem, 5.55vw, 6.45rem);
    line-height: 0.92;
}

#app .periscope-ai-hero__summary {
    max-width: 560px;
    font-size: clamp(1.08rem, 1.22vw, 1.22rem);
    line-height: 1.68;
}

#app .periscope-ai-hero__concepts {
    max-width: 570px;
    margin-top: clamp(38px, 4.5vh, 52px);
}

#app .periscope-ai-hero__concepts li {
    padding-top: 18px;
    font-size: 0.9rem;
}

#app .periscope-ai-hero__media {
    z-index: 1;
    width: 100%;
    height: auto;
    min-height: 0;
    aspect-ratio: 16 / 9;
    margin: 0;
    overflow: hidden;
    border: 1px solid rgba(207, 237, 248, 0.16);
    background: #030914;
    box-shadow: 0 30px 76px rgba(0, 0, 0, 0.28);
}

#app .periscope-ai-hero__media::after {
    display: none;
}

#app .periscope-ai-hero__video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    filter: saturate(0.88) contrast(1.04) brightness(0.88);
}

#app .periscope-ai-intro {
    min-height: 0;
    padding: clamp(120px, 10vw, 156px) 0 clamp(66px, 6vw, 88px);
    background: #f7f2f5;
}

#app .periscope-ai-intro__layout {
    display: grid;
    padding-top: 28px;
    border-top: 1px solid rgba(7, 19, 35, 0.24);
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 0 clamp(22px, 2.4vw, 42px);
    align-items: stretch;
}

#app .periscope-ai-intro__layout > div:first-child {
    grid-column: 1 / 8;
}

#app .periscope-ai-intro h2 {
    max-width: 11.2ch;
    font-size: clamp(3.8rem, 5.55vw, 6.55rem);
    line-height: 0.95;
}

#app .periscope-ai-intro__copy {
    align-self: end;
    padding: clamp(62px, 6vw, 92px) 0 6px clamp(36px, 4.4vw, 72px);
    border-left: 1px solid rgba(7, 19, 35, 0.2);
    grid-column: 8 / 13;
}

#app .periscope-ai-intro__copy p {
    max-width: 50ch;
    color: #344451;
    font-size: clamp(1.08rem, 1.2vw, 1.2rem);
    font-weight: 400;
    line-height: 1.76;
    letter-spacing: normal;
}

#app .periscope-ai-workflow {
    padding: 0 0 clamp(132px, 11vw, 174px);
    color: #071323;
    background: #f7f2f5;
}

#app .periscope-ai-workflow__list {
    display: grid;
    padding-bottom: 0;
    border-top: 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: clamp(64px, 8vw, 128px);
    row-gap: 0;
}

#app .periscope-ai-workflow__step {
    display: grid;
    min-height: 236px;
    padding: 40px 0 44px;
    border-top: 1px solid rgba(7, 19, 35, 0.24);
    border-bottom: 0;
    grid-template-columns: 76px minmax(0, 1fr);
    gap: 22px 28px;
    align-content: start;
    align-items: start;
}

#app .periscope-ai-workflow__step:nth-child(even) {
    transform: none;
}

#app .periscope-ai-workflow__step > span {
    color: #0b2851;
    font-size: clamp(3rem, 4.1vw, 4.6rem);
    font-weight: 450;
    line-height: 0.88;
    letter-spacing: -0.055em;
    grid-row: 1 / span 2;
}

#app .periscope-ai-workflow__step h3 {
    color: #071323;
    padding-top: 2px;
    font-size: clamp(2rem, 2.75vw, 3.2rem);
    line-height: 1.02;
}

#app .periscope-ai-workflow__step p {
    max-width: 46ch;
    color: #455763;
    font-size: 1.06rem;
    line-height: 1.72;
    grid-column: 2;
}

#app .periscope-ai-case-study {
    padding: clamp(126px, 10vw, 164px) 0 clamp(102px, 8vw, 132px);
    background: #0b2851;
}

#app .periscope-ai-case-study__story {
    grid-template-columns: minmax(0, 0.86fr) minmax(470px, 1.14fr);
    gap: clamp(52px, 6vw, 96px);
    align-items: start;
}

#app .periscope-ai-case-study__copy {
    padding-top: clamp(24px, 4vw, 64px);
}

#app .periscope-ai-case-study__copy h2 {
    max-width: 11.4ch;
    margin-bottom: 40px;
    font-size: clamp(3.35rem, 4.9vw, 5.85rem);
    line-height: 0.96;
}

#app .periscope-ai-case-study__media {
    height: clamp(540px, 48vw, 690px);
}

#app .periscope-ai-case-study__media img {
    object-position: 54% center;
    filter: saturate(0.84) contrast(1.08);
}

#app .periscope-ai-metrics {
    position: relative;
    z-index: 2;
    margin-top: 24px;
    padding: 0 clamp(28px, 3.2vw, 52px);
    border-color: rgba(207, 237, 248, 0.26);
    background: #071323;
    grid-template-columns: repeat(3, minmax(0, 0.68fr)) minmax(300px, 1.35fr);
}

#app .periscope-ai-metric {
    min-height: 224px;
    padding-top: 36px;
    padding-bottom: 34px;
}

#app .periscope-ai-metric strong {
    font-size: clamp(3.6rem, 5vw, 5.9rem);
}

#app .periscope-ai-metric > span {
    color: #cbd6db;
    font-size: 0.96rem;
}

#app .periscope-ai-metrics__note {
    margin-top: 22px;
    color: #c2cfd5;
    font-size: 0.86rem;
}

#app .periscope-ai-outputs {
    padding: clamp(124px, 10vw, 166px) 0 clamp(132px, 10vw, 170px);
    background: #f7f2f5;
}

#app .periscope-ai-outputs__layout {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 0 clamp(22px, 2.4vw, 42px);
}

#app .periscope-ai-outputs__heading {
    position: static;
    display: grid;
    padding-bottom: clamp(62px, 6vw, 88px);
    grid-template-columns: repeat(12, minmax(0, 1fr));
    grid-column: 1 / 13;
    gap: 0 clamp(22px, 2.4vw, 42px);
}

#app .periscope-ai-outputs__heading .periscope-ai-eyebrow,
#app .periscope-ai-outputs__heading h2 {
    grid-column: 1 / 8;
}

#app .periscope-ai-outputs__heading h2 {
    max-width: 9.5ch;
    margin-bottom: 0;
    font-size: clamp(3.8rem, 5.35vw, 6.25rem);
}

#app .periscope-ai-outputs__heading > p:last-child {
    align-self: end;
    max-width: 40ch;
    padding-bottom: 5px;
    font-size: 1.12rem;
    grid-column: 9 / 13;
    grid-row: 1 / span 2;
}

#app .periscope-ai-outputs__list {
    display: grid;
    border-top: 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-column: 1 / 13;
}

#app .periscope-ai-outputs__list article {
    min-height: 154px;
    padding: 32px clamp(28px, 4vw, 64px) 34px 0;
    border-top: 1px solid rgba(7, 19, 35, 0.25);
    border-bottom: 0;
    grid-template-columns: 54px minmax(0, 1fr);
    gap: 20px;
}

#app .periscope-ai-outputs__list article:nth-child(even) {
    padding-right: 0;
    padding-left: clamp(28px, 4vw, 64px);
    border-left: 1px solid rgba(7, 19, 35, 0.2);
}

#app .periscope-ai-outputs__list article:nth-last-child(-n + 2) {
    border-bottom: 1px solid rgba(7, 19, 35, 0.25);
}

#app .periscope-ai-outputs__list h3 {
    font-size: clamp(1.65rem, 2vw, 2.2rem);
}

#app .periscope-ai-outputs__list p {
    color: #455763;
    font-size: 1.04rem;
}

#app .periscope-ai-principles {
    padding: clamp(126px, 10vw, 164px) 0 clamp(28px, 2.5vw, 40px);
    background: #071323;
}

#app .periscope-ai-principles__layout {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 0;
    align-items: stretch;
}

#app .periscope-ai-principle {
    position: relative;
}

#app .periscope-ai-principle--primary {
    padding: clamp(64px, 5.5vw, 86px) clamp(68px, 6vw, 96px) clamp(84px, 7vw, 112px);
    background: #0b2851;
    grid-column: 1 / 9;
    grid-row: 1;
}

#app .periscope-ai-principle--secondary {
    align-self: end;
    padding: clamp(54px, 5vw, 78px) 0 clamp(52px, 5vw, 76px) clamp(58px, 5.6vw, 94px);
    border-top: 1px solid rgba(207, 237, 248, 0.32);
    background: #071323;
    grid-column: 7 / 13;
    grid-row: 1;
}

#app .periscope-ai-principle h2 {
    max-width: 9.2ch;
    font-size: clamp(3.45rem, 4.85vw, 5.8rem);
}

#app .periscope-ai-principle h3 {
    max-width: 10.5ch;
    font-size: clamp(2.85rem, 3.85vw, 4.55rem);
}

#app .periscope-ai-applications {
    padding: 0 0 clamp(138px, 11vw, 178px);
    background: #071323;
}

#app .periscope-ai-applications__layout {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 0 clamp(22px, 2.4vw, 42px);
    align-items: start;
    padding-top: clamp(62px, 5.5vw, 82px);
    border-top: 1px solid rgba(207, 237, 248, 0.24);
}

#app .periscope-ai-applications__copy {
    grid-column: 1 / 9;
}

#app .periscope-ai-applications__copy h2 {
    max-width: 12ch;
    font-size: clamp(3.7rem, 5.45vw, 6.45rem);
}

#app .periscope-ai-applications__copy > p:last-child {
    max-width: 60ch;
    font-size: 1.1rem;
}

#app .periscope-ai-applications__list {
    display: grid;
    margin-top: clamp(62px, 6vw, 88px);
    border-top: 1px solid rgba(207, 237, 248, 0.3);
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-column: 1 / 13;
}

#app .periscope-ai-applications__list span {
    display: flex;
    min-height: 126px;
    padding: 28px clamp(28px, 4vw, 64px) 30px 0;
    align-items: center;
    font-size: clamp(1.75rem, 2.5vw, 2.7rem);
}

#app .periscope-ai-applications__list span:nth-child(even) {
    padding-right: 0;
    padding-left: clamp(28px, 4vw, 64px);
    border-left: 1px solid rgba(207, 237, 248, 0.2);
}

#app .periscope-ai-cta {
    padding: clamp(126px, 11vw, 176px) 0;
}

#app .periscope-ai-cta h2 {
    font-size: clamp(3.8rem, 5.9vw, 7rem);
}

@media (max-width: 1100px) {
    #app .periscope-ai-hero {
        padding: 106px 0 76px;
    }

    #app .periscope-ai-hero__layout {
        width: min(calc(100% - clamp(40px, 8vw, 148px)), 1420px);
        min-height: 0;
        grid-template-columns: 1fr;
        gap: 48px;
    }

    #app .periscope-ai-hero__copy {
        padding: 0;
    }

    #app .periscope-ai-hero h1 {
        max-width: 10.2ch;
    }

    #app .periscope-ai-hero__media {
        width: 100%;
        height: auto;
        min-height: 0;
        aspect-ratio: 16 / 9;
        margin: 0;
    }

    #app .periscope-ai-hero__media::after {
        background: linear-gradient(0deg, rgba(3, 9, 20, 0.68), transparent 22%);
    }

    #app .periscope-ai-intro {
        min-height: 0;
    }

    #app .periscope-ai-intro__layout > div:first-child {
        grid-column: 1 / 8;
    }

    #app .periscope-ai-intro__copy {
        padding-top: 72px;
        grid-column: 8 / 13;
    }

    #app .periscope-ai-workflow__list {
        column-gap: 64px;
    }

    #app .periscope-ai-workflow__step:nth-child(even) {
        transform: none;
    }

    #app .periscope-ai-case-study__story {
        grid-template-columns: 1fr;
    }

    #app .periscope-ai-case-study__copy {
        padding-top: 0;
    }

    #app .periscope-ai-case-study__media {
        height: min(68vw, 650px);
    }

    #app .periscope-ai-metrics {
        margin-top: 24px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    #app .periscope-ai-metric:nth-child(2) {
        border-right: 0;
    }

    #app .periscope-ai-metric:nth-child(3) {
        padding-left: 0;
    }

    #app .periscope-ai-principle--primary {
        padding-right: 72px;
        padding-left: 72px;
    }

    #app .periscope-ai-principle--secondary {
        padding-left: 58px;
    }
}

@media (max-width: 820px) {
    #app .periscope-ai-shell,
    #app .periscope-ai-hero__layout {
        width: calc(100% - 40px);
    }

    #app .periscope-ai-hero {
        padding-top: 104px;
        padding-bottom: 68px;
    }

    #app .periscope-ai-hero__layout {
        gap: 40px;
    }

    #app .periscope-ai-hero__media {
        height: auto;
        aspect-ratio: 16 / 9;
    }

    #app .periscope-ai-intro__layout {
        display: block;
    }

    #app .periscope-ai-intro__copy {
        margin-top: 54px;
        padding: 40px 0 0;
        border-top: 1px solid rgba(7, 19, 35, 0.2);
        border-left: 0;
    }

    #app .periscope-ai-workflow__list {
        display: block;
        padding-bottom: 0;
    }

    #app .periscope-ai-workflow__step,
    #app .periscope-ai-workflow__step:nth-child(even) {
        min-height: 0;
        padding: 38px 0 48px;
        transform: none;
    }

    #app .periscope-ai-workflow__step:last-child {
        border-bottom: 1px solid rgba(7, 19, 35, 0.24);
    }

    #app .periscope-ai-outputs__heading {
        display: block;
    }

    #app .periscope-ai-outputs__heading > p:last-child {
        max-width: 48ch;
        margin-top: 34px;
    }

    #app .periscope-ai-outputs__list {
        grid-template-columns: 1fr;
    }

    #app .periscope-ai-outputs__list article,
    #app .periscope-ai-outputs__list article:nth-child(even) {
        min-height: 0;
        padding: 28px 0 30px;
        border-left: 0;
    }

    #app .periscope-ai-outputs__list article:nth-last-child(2) {
        border-bottom: 0;
    }

    #app .periscope-ai-principles__layout {
        display: block;
    }

    #app .periscope-ai-principle--primary {
        padding: 64px 48px 84px;
    }

    #app .periscope-ai-principle--secondary {
        margin-top: 44px;
        padding: 48px 0 0;
    }

    #app .periscope-ai-applications__copy {
        grid-column: 1 / 13;
    }
}

@media (max-width: 620px) {
    #app .periscope-ai-shell,
    #app .periscope-ai-hero__layout {
        width: calc(100% - 36px);
    }

    #app .periscope-ai-hero h1 {
        font-size: clamp(3.15rem, 15.4vw, 4.5rem);
    }

    #app .periscope-ai-hero__concepts {
        display: block;
    }

    #app .periscope-ai-hero__concepts li {
        padding: 14px 0;
        border-bottom: 1px solid rgba(207, 237, 248, 0.16);
    }

    #app .periscope-ai-hero__media {
        height: auto;
        aspect-ratio: 16 / 9;
    }

    #app .periscope-ai-hero__video {
        object-position: center;
    }

    #app .periscope-ai-intro {
        padding-top: 96px;
        padding-bottom: 52px;
    }

    #app .periscope-ai-intro h2,
    #app .periscope-ai-case-study__copy h2,
    #app .periscope-ai-outputs__heading h2,
    #app .periscope-ai-applications__copy h2,
    #app .periscope-ai-cta h2 {
        font-size: clamp(3rem, 13vw, 4.45rem);
    }

    #app .periscope-ai-workflow {
        padding-top: 0;
        padding-bottom: 116px;
    }

    #app .periscope-ai-workflow__step {
        grid-template-columns: 54px minmax(0, 1fr);
        gap: 18px 20px;
    }

    #app .periscope-ai-workflow__step > span {
        font-size: 2.8rem;
    }

    #app .periscope-ai-case-study {
        padding-top: 108px;
    }

    #app .periscope-ai-case-study__media {
        height: min(112vw, 520px);
    }

    #app .periscope-ai-metrics {
        margin-top: 18px;
        padding: 0 24px;
        grid-template-columns: 1fr;
    }

    #app .periscope-ai-metric,
    #app .periscope-ai-metric + .periscope-ai-metric,
    #app .periscope-ai-metric:nth-child(3) {
        min-height: 158px;
        padding: 26px 0;
        border-right: 0;
        border-bottom: 1px solid rgba(207, 237, 248, 0.2);
    }

    #app .periscope-ai-metric:last-child {
        border-bottom: 0;
    }

    #app .periscope-ai-outputs {
        padding-top: 108px;
        padding-bottom: 124px;
    }

    #app .periscope-ai-principles {
        padding-top: 108px;
        padding-bottom: 24px;
    }

    #app .periscope-ai-applications {
        padding-top: 0;
        padding-bottom: 124px;
    }

    #app .periscope-ai-applications__layout {
        padding-top: 56px;
    }

    #app .periscope-ai-principle--primary {
        padding: 52px 28px 68px;
    }

    #app .periscope-ai-principle h2 {
        font-size: clamp(3rem, 13vw, 4.35rem);
    }

    #app .periscope-ai-principle h3 {
        font-size: clamp(2.7rem, 11.5vw, 3.9rem);
    }

    #app .periscope-ai-applications__list {
        grid-template-columns: 1fr;
    }

    #app .periscope-ai-applications__list span,
    #app .periscope-ai-applications__list span:nth-child(even) {
        min-height: 92px;
        padding: 22px 0;
        border-left: 0;
        font-size: 1.65rem;
    }

    #app .periscope-ai-cta {
        padding-top: 108px;
        padding-bottom: 112px;
    }
}

@media (prefers-reduced-motion: reduce) {
    #app .periscope-ai-hero__video {
        transform: none;
    }
}
</style>
